import React, { useEffect, useCallback } from 'react';
import { imageUrl } from '../lib/images';

export default function Lightbox({ images = [], currentIndex = 0, onClose, onNavigate }) {
  const hasImages = images && images.length > 0;
  const currentImg = hasImages ? images[currentIndex] : null;

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        if (images.length > 1) {
          onNavigate((currentIndex + 1) % images.length);
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        if (images.length > 1) {
          onNavigate((currentIndex - 1 + images.length) % images.length);
        }
      }
    },
    [currentIndex, images.length, onClose, onNavigate]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyDown]);

  if (!hasImages) return null;

  const imgSrc = typeof currentImg === 'string' ? currentImg : currentImg?.url || currentImg?.src;
  const imgAlt = typeof currentImg === 'object' ? currentImg?.alt || currentImg?.title || '' : '';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-200 select-none"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-3 text-white/80 hover:text-white bg-slate-900/60 hover:bg-slate-900 rounded-full transition-colors cursor-pointer"
        aria-label="Luk billedvisning"
      >
        <i className="fa-solid fa-xmark text-xl" aria-hidden="true" />
      </button>

      {/* Counter */}
      {images.length > 1 && (
        <div className="absolute top-4 left-4 z-50 px-3 py-1.5 bg-slate-900/70 text-white text-xs font-semibold rounded-md border border-white/10">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Prev button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate((currentIndex - 1 + images.length) % images.length);
          }}
          className="absolute left-3 sm:left-6 z-50 p-3 sm:p-4 text-white/80 hover:text-white bg-slate-900/60 hover:bg-slate-900 rounded-full transition-colors cursor-pointer focus:outline-hidden"
          aria-label="Forrige billede"
        >
          <i className="fa-solid fa-chevron-left text-lg sm:text-xl" aria-hidden="true" />
        </button>
      )}

      {/* Center Image */}
      <div
        className="relative max-w-5xl max-h-[85vh] flex flex-col items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={imageUrl(imgSrc, 'width=1600,quality=90,format=auto')}
          alt={imgAlt || 'Galleri billede'}
          className="max-h-[80vh] max-w-full object-contain rounded-md shadow-2xl transition-transform"
        />
        {imgAlt && (
          <p className="mt-3 text-sm text-slate-300 text-center font-medium bg-black/40 px-4 py-1.5 rounded-md">
            {imgAlt}
          </p>
        )}
      </div>

      {/* Next button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNavigate((currentIndex + 1) % images.length);
          }}
          className="absolute right-3 sm:right-6 z-50 p-3 sm:p-4 text-white/80 hover:text-white bg-slate-900/60 hover:bg-slate-900 rounded-full transition-colors cursor-pointer focus:outline-hidden"
          aria-label="Næste billede"
        >
          <i className="fa-solid fa-chevron-right text-lg sm:text-xl" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
